import React, { forwardRef } from 'react';

import type { ButtonBaseProps } from '@mui/material';
import { ButtonBase } from '@mui/material';
import clsx from 'clsx';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  /* Styles applied if disabled=true */
  disabled: {},
  /* Styles applied to the `label` wrapper element. */
  label: {
    alignItems: 'inherit',
    color: 'inherit',
    display: 'inherit',
    justifyContent: 'inherit',
    width: '100%', // Ensure the correct width for iOS Safari
  },
  /* Styles applied to the root element. */
  root: {
    ...getTypographyForSx(theme.typography.button),
    '&.Mui-disabled': {
      '&:active': {
        backgroundColor: 'transparent',
        color: theme.palette.action.disabled,
      },
      '&:hover': {
        backgroundColor: 'transparent',
      },
      color: theme.palette.action.disabled,
    },
    '&:active': {
      color: theme.appColors.primary,
      transition: '&:active 1s',
    },
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    borderRadius: 3,
    boxSizing: 'border-box',
    color: theme.appColors.iconsType,
    padding: theme.spacing(0.5),
    textDecoration: 'none',
  },
}));

/**
 * The difference between this component and ToggleButton.tsx is that this button cannot be "selected", while the ToggleButton can.
 */
const ToolbarButtonWithRef = forwardRef(function ToolbarButton(
  props: ButtonBaseProps,
  ref: React.Ref<HTMLButtonElement>
): JSX.Element {
  const { children, className, disabled, ...rest } = props;
  const { classes } = useStyles();

  return (
    <ButtonBase
      ref={ref}
      className={clsx(classes.root, { [classes.disabled]: disabled }, className)}
      disabled={disabled}
      {...rest}
    >
      <span className={classes.label}>{children}</span>
    </ButtonBase>
  );
});

export const ToolbarButton = React.memo(ToolbarButtonWithRef);
